import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function AddRoute() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    route_type: '',
    route_name: '',
    selected_locations: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchVehicles();
      fetchPickupLocations();
    }
  }, [schoolData?.id]);

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('transport_vehicles')
        .select('id, bus_number, capacity, driver_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('bus_number', { ascending: true });

      if (error) {
        console.error('Error fetching vehicles:', error);
        return;
      }

      setVehicles(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchPickupLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('pickup_locations')
        .select('id, location_name, address')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('location_name', { ascending: true });

      if (error) {
        console.error('Error fetching pickup locations:', error);
        return;
      }

      setPickupLocations(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear success message
    if (success) {
      setSuccess('');
    }
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const selected = prev.selected_locations;
      const isSelected = selected.includes(locationId);
      
      return {
        ...prev,
        selected_locations: isSelected
          ? selected.filter(id => id !== locationId)
          : [...selected, locationId]
      };
    });
    
    // Clear error
    if (errors.selected_locations) {
      setErrors(prev => ({
        ...prev,
        selected_locations: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vehicle_id) {
      newErrors.vehicle_id = 'Please select a vehicle';
    }
    
    if (!formData.route_type) {
      newErrors.route_type = 'Please select a route type';
    }
    
    if (formData.selected_locations.length === 0) {
      newErrors.selected_locations = 'Please select at least one pickup location';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSuccess('');
    
    try {
      // First, create the route
      const routeData = {
        school_id: schoolData.id,
        vehicle_id: parseInt(formData.vehicle_id, 10),
        route_type: formData.route_type,
        route_name: formData.route_name.trim() || null,
        is_active: true
      };
      
      const { data: routeResult, error: routeError } = await supabase
        .from('routes')
        .insert([routeData])
        .select()
        .single();
      
      if (routeError) {
        console.error('Error creating route:', routeError);
        alert('Error creating route: ' + routeError.message);
        return;
      }
      
      // Then, create the route_pickup_locations entries
      if (formData.selected_locations.length > 0) {
        const routeLocationData = formData.selected_locations.map((locationId, index) => ({
          route_id: routeResult.id,
          pickup_location_id: locationId,
          sequence_order: index + 1
        }));
        
        const { error: locationError } = await supabase
          .from('route_pickup_locations')
          .insert(routeLocationData);
        
        if (locationError) {
          console.error('Error adding pickup locations to route:', locationError);
          // Rollback: delete the route if location insertion fails
          await supabase.from('routes').delete().eq('id', routeResult.id);
          alert('Error adding pickup locations: ' + locationError.message);
          return;
        }
      }
      
      setSuccess('Route created successfully!');
      
      // Reset form
      setFormData({
        vehicle_id: '',
        route_type: '',
        route_name: '',
        selected_locations: []
      });
      
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while creating the route. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const selectedVehicle = vehicles.find(v => v.id === parseInt(formData.vehicle_id, 10));

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>Add Route</h1>
        </div>
        
        <div className="content-body">
          <div className="add-school-content">
            <div className="school-form-container">
              <form onSubmit={handleSubmit} className="school-form">
                <div className="form-section">
                  <h3>Route Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="vehicle_id">
                      Select Vehicle <span className="asterisk">*</span>
                    </label>
                    <select
                      id="vehicle_id"
                      name="vehicle_id"
                      value={formData.vehicle_id}
                      onChange={handleInputChange}
                      className={errors.vehicle_id ? 'error' : ''}
                      required
                    >
                      <option value="">-- Select a vehicle --</option>
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.bus_number} {vehicle.driver_name ? `(Driver: ${vehicle.driver_name})` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.vehicle_id && (
                      <span className="error-text">{errors.vehicle_id}</span>
                    )}
                    {vehicles.length === 0 && (
                      <span className="error-text" style={{ color: '#6B7280', fontSize: '13px' }}>
                        No vehicles available. Please add a vehicle first.
                      </span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="route_type">
                      Route Type <span className="asterisk">*</span>
                    </label>
                    <select
                      id="route_type"
                      name="route_type"
                      value={formData.route_type}
                      onChange={handleInputChange}
                      className={errors.route_type ? 'error' : ''}
                      required
                    >
                      <option value="">-- Select route type --</option>
                      <option value="1 Way Morning">1 Way Morning</option>
                      <option value="1 Way Evening">1 Way Evening</option>
                      <option value="2 Way">2 Way</option>
                    </select>
                    {errors.route_type && (
                      <span className="error-text">{errors.route_type}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="route_name">Route Name (Optional)</label>
                    <input
                      type="text"
                      id="route_name"
                      name="route_name"
                      value={formData.route_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Route A, North Route, etc."
                    />
                    <small style={{ color: '#6B7280', fontSize: '12px' }}>
                      Optional: Give a name to identify this route easily
                    </small>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Select Pickup Locations</h3>
                  
                  {pickupLocations.length === 0 ? (
                    <div style={{ 
                      padding: '1.5rem', 
                      background: '#FEF3C7', 
                      borderRadius: '8px',
                      border: '1px solid #FCD34D',
                      marginBottom: '1rem'
                    }}>
                      <p style={{ color: '#92400E', margin: 0 }}>
                        No pickup locations available. Please add pickup locations first.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/school/add-pickup-location')}
                        style={{
                          marginTop: '0.75rem',
                          background: '#FF8C42',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                      >
                        Add Pickup Location
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <label style={{ marginBottom: '0.75rem', display: 'block' }}>
                          Pickup Locations <span className="asterisk">*</span>
                          <span style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                            (Select one or more locations)
                          </span>
                        </label>
                        {errors.selected_locations && (
                          <span className="error-text" style={{ display: 'block', marginBottom: '0.75rem' }}>
                            {errors.selected_locations}
                          </span>
                        )}
                        <div style={{
                          border: errors.selected_locations ? '2px solid #EF4444' : '1px solid #E5E7EB',
                          borderRadius: '12px',
                          padding: '1rem',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          background: '#FFFFFF',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          {pickupLocations.map((location, index) => {
                            const isSelected = formData.selected_locations.includes(location.id);
                            return (
                              <div
                                key={location.id}
                                onClick={() => handleLocationToggle(location.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  padding: '1rem',
                                  marginBottom: index < pickupLocations.length - 1 ? '0.75rem' : '0',
                                  cursor: 'pointer',
                                  borderRadius: '10px',
                                  background: isSelected ? '#EFF6FF' : '#FFFFFF',
                                  border: isSelected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                                  transition: 'all 0.2s ease',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = '#F9FAFB';
                                    e.currentTarget.style.borderColor = '#D1D5DB';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = '#FFFFFF';
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                              >
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  minWidth: '24px',
                                  marginRight: '1rem',
                                  marginTop: '0.125rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: isSelected ? '2px solid #3B82F6' : '2px solid #D1D5DB',
                                  background: isSelected ? '#3B82F6' : '#FFFFFF',
                                  transition: 'all 0.2s ease'
                                }}>
                                  {isSelected && (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: '600', 
                                    fontSize: '0.95rem',
                                    color: '#1F2937',
                                    marginBottom: '0.375rem',
                                    lineHeight: '1.4'
                                  }}>
                                    {location.location_name}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.875rem', 
                                    color: '#6B7280',
                                    lineHeight: '1.5'
                                  }}>
                                    {location.address}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '0.5rem',
                                    right: '0.5rem',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#3B82F6'
                                  }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{
                          marginTop: '1rem',
                          padding: '0.75rem 1rem',
                          background: formData.selected_locations.length > 0 ? '#EFF6FF' : '#F9FAFB',
                          borderRadius: '8px',
                          border: formData.selected_locations.length > 0 ? '1px solid #BFDBFE' : '1px solid #E5E7EB'
                        }}>
                          <span style={{ 
                            fontSize: '0.875rem', 
                            color: formData.selected_locations.length > 0 ? '#1E40AF' : '#6B7280',
                            fontWeight: formData.selected_locations.length > 0 ? '600' : '500'
                          }}>
                            {formData.selected_locations.length > 0 
                              ? `✓ ${formData.selected_locations.length} location${formData.selected_locations.length > 1 ? 's' : ''} selected`
                              : 'No locations selected'
                            }
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {success && (
                  <div className="success-message" style={{ 
                    backgroundColor: '#D1FAE5', 
                    color: '#065F46', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '24px',
                    border: '1px solid #A7F3D0'
                  }}>
                    {success}
                  </div>
                )}
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => navigate('/school/dashboard')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn" 
                    disabled={isSubmitting || vehicles.length === 0 || pickupLocations.length === 0}
                    style={{ backgroundColor: '#FF8C42', color: 'white' }}
                  >
                    {isSubmitting ? 'Creating Route...' : 'Create Route'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddRoute;

